/**
 * Hook for managing canvas image loading and auto-import functionality.
 * Uses global store for canvas images to share state across components.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useAppStore, getAppStore } from '@/store';
import {
  generateCanvasImage,
  type CanvasImage,
} from '@/utils/canvas/canvasUtils';
import uploadAndUpdateInput from '@/utils/canvas/uploadAndUpdateInput';
import {
  getLoadedFlowId,
  setLoadedFlowId,
} from '@/utils/canvas/canvasLoadState';
import { InputType } from '@/types';
import useAuth from './useAuth';

import type { IAppStore } from '@/store';

interface UseCanvasImagesOptions {
  /** Flow ID that triggers canvas loading when changed */
  flowId: string | undefined;
}

interface UseCanvasImagesReturn {
  /** Available canvas images (from global store) */
  images: CanvasImage[];
  /** Whether canvas selector modal is open */
  isOpen: boolean;
  /** Open selector for a specific input */
  openSelector: (inputId: string) => void;
  /** Close selector */
  closeSelector: () => void;
  /** Handle image selection from modal */
  handleSelect: (image: CanvasImage) => Promise<void>;
}

const selector = (store: IAppStore) => ({
  canvasImages: store.canvasImages,
  setCanvasImages: store.setCanvasImages,
  updateInput: store.updateInput,
  addPendingUpload: store.addPendingUpload,
});

const useCanvasImages = ({
  flowId,
}: UseCanvasImagesOptions): UseCanvasImagesReturn => {
  const { isAuthorized } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [pendingInputId, setPendingInputId] = useState<string | null>(null);
  const hasLoaded = useRef<string | null>(null);

  // Use global store for canvas images
  const { canvasImages, setCanvasImages, updateInput, addPendingUpload } =
    useAppStore(selector);

  // Load canvas images and auto-import single image
  useEffect(() => {
    if (!flowId) return;
    if (hasLoaded.current || getLoadedFlowId()) return;

    hasLoaded.current = flowId;

    const loadImages = async () => {
      const { inputs } = getAppStore();
      const hasMediaInputs = inputs.some(
        (input) =>
          input.type === InputType.IMAGE || input.type === InputType.VIDEO
      );
      if (!hasMediaInputs) return;

      try {
        // Generate canvas image if canvas has layers
        const canvasImage = await generateCanvasImage();

        if (canvasImage) {
          setLoadedFlowId(flowId);

          // Auto-import the canvas image into a SINGLE media input, and only
          // when none is filled yet. `buildInputsFromPreset` may already have
          // carried a media src into the first slot (preserved across presets);
          // filling the first *empty* slot on top of that would put the same
          // canvas image in two upload areas. So skip the auto-import entirely
          // once any media slot is occupied — the canvas image is still offered
          // via the selector for the remaining slots.
          const latestInputs = getAppStore().inputs;
          const mediaInputs = latestInputs.filter(
            (input) =>
              input.type === InputType.IMAGE || input.type === InputType.VIDEO
          );
          const noMediaFilled = mediaInputs.every((input) => !input.src);
          const firstEmpty = mediaInputs.find((input) => !input.src);

          if (noMediaFilled && firstEmpty) {
            const storeUpdateInput = getAppStore().updateInput;
            await uploadAndUpdateInput({
              inputId: firstEmpty.id,
              image: canvasImage,
              updateInput: storeUpdateInput,
              isAuthorized,
              addPendingUpload,
            });
          }

          // Also add to available images for manual selection
          setCanvasImages([canvasImage]);
        }
      } catch {
        // Silent fail - canvas images are optional
      }
    };

    loadImages().finally(() => {
      if (!getLoadedFlowId()) hasLoaded.current = null;
    });
  }, [flowId, setCanvasImages, isAuthorized, addPendingUpload]);

  const openSelector = useCallback(
    async (inputId: string) => {
      if (canvasImages.length === 0) return;

      // Import directly — generateCanvasImage produces a single flattened image
      await uploadAndUpdateInput({
        inputId,
        image: canvasImages[0],
        updateInput,
        isAuthorized,
        addPendingUpload,
        trackUpload: true,
      });
    },
    [canvasImages, updateInput, isAuthorized, addPendingUpload]
  );

  const closeSelector = useCallback(() => {
    setIsOpen(false);
    setPendingInputId(null);
  }, []);

  const handleSelect = useCallback(
    async (image: CanvasImage) => {
      if (!pendingInputId) return;

      const inputId = pendingInputId;
      closeSelector();

      await uploadAndUpdateInput({
        inputId,
        image,
        updateInput,
        isAuthorized,
        addPendingUpload,
        trackUpload: true,
      });
    },
    [pendingInputId, updateInput, closeSelector, isAuthorized, addPendingUpload]
  );

  return {
    images: canvasImages,
    isOpen,
    openSelector,
    closeSelector,
    handleSelect,
  };
};

export default useCanvasImages;
